import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Table, Tag, Button, Space, Typography, Spin, Empty, message } from 'antd';
import { MessageOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';

const { Text, Paragraph } = Typography;

const STATUS_COLOR = {
  PENDING: 'orange', ACCEPTED: 'green', REJECTED: 'red', CANCELLED: 'default',
};
const STATUS_LABEL = {
  PENDING: 'Pendente', ACCEPTED: 'Aceito', REJECTED: 'Recusado', CANCELLED: 'Cancelado',
};

function fmt(dt) { return dt ? dayjs(dt).format('DD/MM/YY HH:mm') : '—'; }

// Modal admin que lista convites da comunidade (filtro por direction/status)
// com botao pra abrir chat de cada convite.
export default function CommunityInvitesModal({ open, onClose, direction, status, title }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInvite, setChatInvite] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (direction) params.direction = direction;
      if (status) params.status = status;
      const r = await api.get('/monitoring/community-invites', { params });
      setItems(r.data.items || []);
    } catch (e) {
      message.error('Erro ao carregar convites');
    } finally { setLoading(false); }
  }, [direction, status]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const openChat = async (inv) => {
    setChatOpen(true); setChatInvite(inv); setChatMsgs([]); setChatLoading(true);
    try {
      const r = await api.get(`/monitoring/community-invites/${inv.id}/messages`);
      setChatMsgs(r.data.messages || []);
    } catch (e) {
      message.error('Erro ao carregar chat');
    } finally { setChatLoading(false); }
  };

  const columns = [
    {
      title: 'Direção', key: 'dir', width: 130,
      render: (_, r) => r.invited_by_team
        ? <Tag icon={<TeamOutlined />} color="blue">Time → Jogador</Tag>
        : <Tag icon={<UserOutlined />} color="green">Jogador → Time</Tag>,
    },
    {
      title: 'De → Para', key: 'from_to',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.invited_by_team ? (
            <>
              <div><strong>{r.team_name || `#${r.team_id}`}</strong>{r.futezap_game_name ? ` · ${r.futezap_game_name}` : ''}</div>
              <div style={{ color: '#888' }}>→ {r.player_name || `cp#${r.cp_id}`} ({r.player_phone || '—'})</div>
            </>
          ) : (
            <>
              <div><strong>{r.player_name || `cp#${r.cp_id}`}</strong> ({r.player_phone || '—'})</div>
              <div style={{ color: '#888' }}>→ {r.team_name || `#${r.team_id}`}{r.futezap_game_name ? ` · ${r.futezap_game_name}` : ''}</div>
            </>
          )}
        </div>
      ),
    },
    { title: 'Tipo', dataIndex: 'kind', key: 'kind', width: 80,
      render: k => k === 'GAME' ? <Tag>Jogo</Tag> : <Tag color="purple">Conhecer</Tag> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag> },
    { title: 'Chat', key: 'chat', width: 90, align: 'center',
      render: (_, r) => r.message_count > 0
        ? <Tag color="cyan">{r.message_count} msg</Tag>
        : <Text type="secondary" style={{ fontSize: 11 }}>—</Text> },
    { title: 'Criado', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: fmt },
    { title: 'Respondido', dataIndex: 'responded_at', key: 'responded_at', width: 120,
      render: fmt },
    {
      title: '', key: 'actions', width: 100,
      render: (_, r) => (
        <Button size="small" icon={<MessageOutlined />} onClick={() => openChat(r)}>
          Ver chat
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={title || 'Convites da comunidade'}
        open={open}
        onCancel={onClose}
        footer={null}
        width="min(1100px, 96vw)"
        destroyOnClose
      >
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 900 }}
          locale={{ emptyText: <Empty description="Nenhum convite" /> }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: 8, background: '#fafafa', borderRadius: 4 }}>
                {r.message && <Paragraph style={{ marginBottom: 4 }}>
                  <strong>Mensagem inicial:</strong> {r.message}
                </Paragraph>}
                {r.last_message_body && <Paragraph style={{ marginBottom: 4 }}>
                  <strong>Última msg do chat:</strong> {r.last_message_body} <Text type="secondary" style={{ fontSize: 11 }}>({fmt(r.last_message_at)})</Text>
                </Paragraph>}
                <div style={{ fontSize: 11, color: '#666' }}>
                  invite id: {r.id} · team_id: {r.team_id} · cp_id: {r.cp_id}
                  {r.game_id ? ` · game_id: ${r.game_id}` : ''}
                </div>
              </div>
            ),
          }}
        />
      </Modal>

      <Modal
        title={chatInvite
          ? `Chat: ${chatInvite.player_name || '?'} ↔ ${chatInvite.team_name || '?'}`
          : 'Chat'}
        open={chatOpen}
        onCancel={() => setChatOpen(false)}
        footer={<Button onClick={() => setChatOpen(false)}>Fechar</Button>}
        width="min(640px, 96vw)"
        destroyOnClose
      >
        {chatLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : chatMsgs.length === 0 ? (
          <Empty description="Ainda sem mensagens no chat desse convite" />
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: 4 }}>
            {chatMsgs.map(m => {
              const isPlayer = m.sender_type === 'player';
              return (
                <div key={m.id} style={{
                  display: 'flex',
                  justifyContent: isPlayer ? 'flex-start' : 'flex-end',
                  marginBottom: 8,
                }}>
                  <div style={{
                    maxWidth: '80%',
                    background: isPlayer ? '#f0f0f0' : '#dcf8c6',
                    padding: '8px 12px',
                    borderRadius: 12,
                    fontSize: 13,
                  }}>
                    <div style={{ fontSize: 10, color: '#666', marginBottom: 2 }}>
                      {isPlayer ? '👤 Jogador' : '🛡 Time'} · {fmt(m.created_at)}
                      {m.read_at && <span style={{ marginLeft: 6, color: '#25D366' }}>✓✓ lida</span>}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}
